// Text-quality checks for free text a client types during onboarding
// (business address, business name, contact name).
//
// These matter most when Google Places is unavailable and the address is typed
// by hand, since nothing else is validating that field. Every check here is
// tuned for precision rather than recall: a signup that gets through still has
// to be approved by an admin, so letting a borderline entry pass is much
// cheaper than blocking a real business ("Cockburn St", "Scunthorpe Rd",
// "Hell's Kitchen" must all survive).

// Look-alike characters people use to slip words past a filter.
const LEET_MAP = {
  0: "o",
  1: "i",
  3: "e",
  4: "a",
  5: "s",
  7: "t",
  8: "b",
  "@": "a",
  $: "s",
  "!": "i",
  "|": "l",
  "+": "t",
};

// Strong profanity and slurs only. Mild words (damn, hell, crap, ass, dick...)
// are deliberately absent: they appear in real street and place names, and a
// false positive here blocks a paying client from signing up.
const PROFANITY = [
  "fuck",
  "fuk",
  "fuc",
  "motherfucker",
  "shit",
  "bullshit",
  "bitch",
  "asshole",
  "arsehole",
  "cunt",
  "dickhead",
  "wanker",
  "twat",
  "whore",
  "slut",
  "nigger",
  "nigga",
  "faggot",
  "fag",
  "retard",
  "pussy",
  "penis",
  "dildo",
  "blowjob",
  "handjob",
  "jerkoff",
  "boner",
  "titties",
  "wtf",
];

// Words that only ever mean "I did not want to fill this in".
const PLACEHOLDER_WORDS = new Set([
  "test",
  "tests",
  "testing",
  "asdf",
  "asdfasdf",
  "qwerty",
  "none",
  "na",
  "nil",
  "null",
  "undefined",
  "nothing",
  "abc",
  "aaa",
  "xxx",
  "xyz",
  "dummy",
  "sample",
  "fake",
  "unknown",
  "blah",
]);

// Letter rows only. Digit runs are excluded on purpose: "12345 Main St" is a
// perfectly ordinary address.
const KEYBOARD_ROWS = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];

const KEYBOARD_RUNS = KEYBOARD_ROWS.reduce((runs, row) => {
  const reversed = row.split("").reverse().join("");
  [row, reversed].forEach((sequence) => {
    for (let i = 0; i + 4 <= sequence.length; i += 1) {
      runs.push(sequence.slice(i, i + 4));
    }
  });
  return runs;
}, []);

// "fuck" -> /\bf+u+c+k+\b/ so that "fuuuuck" and "ffuck" are caught while
// "fuchsia" (no word boundary) is not.
const WORD_BOUNDARY = String.raw`\b`;

const PROFANITY_PATTERNS = PROFANITY.map(
  (word) =>
    new RegExp(
      WORD_BOUNDARY +
        word
          .split("")
          .map((char) => `${char}+`)
          .join("") +
        WORD_BOUNDARY
    )
);

// Lowercase, strip accents, undo leetspeak and reduce everything else to single
// spaces, so matching only ever runs against [a-z0-9 ].
const normalize = (text) =>
  String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[0134578@$!|+]/g, (char) => LEET_MAP[char] ?? char)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// "f u c k street" -> "fuck street". Joins runs of three or more single
// letters, which is how separator obfuscation ("f-u-c-k", "f.u.c.k") lands
// after normalize().
const joinSpacedLetters = (text) =>
  text.replace(/\b(?:[a-z] ){2,}[a-z]\b/g, (match) => match.replaceAll(" ", ""));

export const containsProfanity = (text) => {
  const normalized = normalize(text);
  if (!normalized) return false;
  const candidates = [normalized, joinSpacedLetters(normalized)];
  return PROFANITY_PATTERNS.some((pattern) =>
    candidates.some((candidate) => pattern.test(candidate))
  );
};

const words = (text) => normalize(text).split(" ").filter(Boolean);

// True when every word is a filler word ("test test", "n/a", "asdf"), but not
// when a filler word is part of a real name ("Test Kitchen Road").
const isPlaceholderOnly = (text) => {
  const tokens = words(text);
  if (tokens.length === 0) return false;
  // The joined form catches punctuated variants such as "n/a" -> "na".
  return (
    tokens.every((token) => PLACEHOLDER_WORDS.has(token)) ||
    PLACEHOLDER_WORDS.has(tokens.join(""))
  );
};

// Checked per word, never across the whole string: "Texas DFW Blvd" collapses
// to "texasdfw", which contains "asdf" without being a mash.
const isKeyboardMash = (text) =>
  words(text).some((token) => {
    const letters = token.replace(/[^a-z]/g, "");
    return KEYBOARD_RUNS.some((run) => letters.includes(run));
  });

// Rules that mean "this is not real input" regardless of which field it is.
// Returns a reason string, or "" when the text looks like genuine input.
// The character-level rules below run against the raw text rather than the
// normalised form: normalising drops every non-Latin character, which would
// make a Japanese or Hindi address look like a string of digits.
const junkReason = (text) => {
  const raw = String(text ?? "").trim();

  if (containsProfanity(raw)) return "profanity";
  if (isPlaceholderOnly(raw)) return "placeholder";
  if (isKeyboardMash(raw)) return "mash";

  // Four of the same letter in a row ("aaaa"). Digits are exempt: "1111 Main
  // St" is a real address.
  if (/([a-z])\1{3,}/i.test(raw)) return "repeated";

  // Too few distinct characters to be words ("ababab"). Only applied to longer
  // text, so short real names ("3M", "H&M") are left alone.
  const compact = raw.toLowerCase().replace(/\s+/g, "");
  if (compact.length >= 5 && new Set(compact).size < 3) return "repeated";

  if (!/\p{L}/u.test(raw)) return "no-letters";

  return "";
};

/**
 * Validates a hand-typed business address.
 * Returns "" when the address is acceptable, otherwise the message to show.
 */
export const validateBusinessAddress = (address) => {
  const value = String(address ?? "").trim();

  if (!value) return "Please enter business address";
  if (value.length < 6) return "Please enter a complete address";

  const reason = junkReason(value);
  if (reason === "profanity") return "Please enter a valid address without inappropriate language";
  if (reason) return "Please enter a valid street address";

  // A usable business address is never a single word. Only enforced for
  // Latin-script text: addresses in scripts that do not space their words
  // (Japanese, Chinese) legitimately arrive as one token.
  const isLatinText = /[a-z]{4,}/.test(normalize(value));
  if (isLatinText && value.split(/\s+/).filter(Boolean).length < 2) {
    return "Please enter a complete address";
  }

  return "";
};

/**
 * Validates a typed name (business name, contact name).
 * Returns "" when the name is acceptable, otherwise the message to show.
 */
export const validateName = (name, label = "name") => {
  const value = String(name ?? "").trim();

  if (!value) return `Please enter ${label}`;
  if (value.length < 2) return `Please enter a valid ${label}`;

  const reason = junkReason(value);
  if (reason === "profanity") return `Please enter a ${label} without inappropriate language`;
  if (reason) return `Please enter a valid ${label}`;

  return "";
};
